import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Book, Image as ImageIcon, Mail, Palette, Plus, Save, Send, Trash2, Upload } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { PatriaWordmark } from "@/components/patria/PatriaWordmark";
import MyPatriaWordmark from "@/components/patria/MyPatriaWordmark";
import { useUiText } from "@/contexts/UiTextContext";

const TABS = [
  { key: "verses", label: "Scripture verses", icon: Book },
  { key: "branding", label: "Branding & logo", icon: Palette },
  { key: "templates", label: "Notification templates", icon: Mail },
  { key: "email-delivery", label: "Email delivery", icon: Send },
  { key: "text", label: "Text & Headings", icon: Book },
  { key: "outbox", label: "Outbound email log", icon: ImageIcon },
];

export default function Settings() {
  const { reload: reloadPublic } = useSettings();
  const [tab, setTab] = useState("verses");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveSettings = async (partial) => {
    try {
      await api.put("/settings", partial);
      toast.success("Settings saved.");
      await load();
      await reloadPublic();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          Super Admin
        </p>
        <h1 className="text-4xl font-serif text-forest-900 mt-1">Settings</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? "bg-forest-600 text-cream-100"
                : "bg-cream-100 border border-sage-300 text-charcoal hover:bg-sage-200/60"
            }`}
            data-testid={`settings-tab-${t.key}`}
          >
            <t.icon className="h-4 w-4" strokeWidth={1.5} /> {t.label}
          </button>
        ))}
      </div>

      {loading || !settings ? (
        <p className="italic text-charcoal-muted">Loading settings...</p>
      ) : tab === "verses" ? (
        <VersesPanel settings={settings} onSave={saveSettings} />
      ) : tab === "branding" ? (
        <BrandingPanel settings={settings} onSave={saveSettings} reloadPublic={reloadPublic} onReload={load} />
      ) : tab === "templates" ? (
        <TemplatesPanel settings={settings} onSave={saveSettings} />
      ) : tab === "email-delivery" ? (
        <EmailDeliveryPanel />
      ) : tab === "text" ? (
        <TextHeadingsPanel />
      ) : (
        <OutboxPanel />
      )}
    </div>
  );
}

/* -------------------- Verses -------------------- */
function VersesPanel({ settings, onSave }) {
  const [verses, setVerses] = useState(settings.scripture_verses || []);
  const [enabled, setEnabled] = useState(settings.verses_enabled);

  useEffect(() => setVerses(settings.scripture_verses || []), [settings]);
  useEffect(() => setEnabled(settings.verses_enabled), [settings]);

  const update = (idx, patch) => {
    setVerses((v) => v.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  };
  const addVerse = () => {
    setVerses((v) => [
      ...v,
      { id: `v-${Date.now()}`, text: "", attribution: "", enabled: true },
    ]);
  };
  const remove = (idx) => setVerses((v) => v.filter((_, i) => i !== idx));

  return (
    <div className="patria-card p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-forest-900">Scripture Verses</h2>
          <p className="text-sm text-charcoal-soft">
            One verse is displayed on the dashboard, rotated daily.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="verses-global-toggle"
            className="rounded"
          />
          Show verses on dashboard
        </label>
      </div>

      <ul className="space-y-4">
        {verses.map((v, idx) => (
          <li key={v.id || idx} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start" data-testid={`verse-row-${idx}`}>
            <textarea
              className="patria-input sm:col-span-6 min-h-[68px]"
              value={v.text}
              onChange={(e) => update(idx, { text: e.target.value })}
              placeholder="Verse text..."
              data-testid={`verse-text-${idx}`}
            />
            <input
              className="patria-input sm:col-span-3"
              value={v.attribution}
              onChange={(e) => update(idx, { attribution: e.target.value })}
              placeholder="Attribution (e.g., Colossians 3:23)"
              data-testid={`verse-attr-${idx}`}
            />
            <label className="sm:col-span-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={v.enabled}
                onChange={(e) => update(idx, { enabled: e.target.checked })}
                data-testid={`verse-enabled-${idx}`}
                className="rounded"
              />
              Enabled
            </label>
            <button
              className="patria-btn-ghost sm:col-span-1 text-terracotta-600"
              onClick={() => remove(idx)}
              aria-label="Remove verse"
              data-testid={`verse-remove-${idx}`}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </li>
        ))}
      </ul>

      <div className="flex gap-3">
        <button className="patria-btn-secondary" onClick={addVerse} data-testid="verse-add">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add verse
        </button>
        <button
          className="patria-btn-primary"
          onClick={() => onSave({ verses_enabled: enabled, scripture_verses: verses })}
          data-testid="verse-save"
        >
          <Save className="h-4 w-4" strokeWidth={1.5} /> Save verses
        </button>
      </div>
    </div>
  );
}

/* -------------------- Branding -------------------- */
function BrandingPanel({ settings, onSave, reloadPublic, onReload }) {
  const [org, setOrg] = useState(settings.org_name);
  const [welcome, setWelcome] = useState(settings.welcome_message);
  const fileRef = useRef(null);

  useEffect(() => {
    setOrg(settings.org_name);
    setWelcome(settings.welcome_message);
  }, [settings]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/uploads/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded.");
      await onReload();
      await reloadPublic();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
    e.target.value = "";
  };

  return (
    <div className="patria-card p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-serif text-forest-900">Branding</h2>
        <p className="text-sm text-charcoal-soft">
          Set organisation name, welcome message, and header logo. Until a logo is
          uploaded, the PATRIA wordmark is displayed.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            Organisation name
          </label>
          <input
            className="patria-input w-full"
            value={org}
            onChange={(e) => setOrg(e.target.value)}
            data-testid="org-name-input"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-widest font-medium text-charcoal-soft mb-1.5">
            Welcome message (shown on login card)
          </label>
          <textarea
            className="patria-input w-full min-h-[80px]"
            value={welcome}
            onChange={(e) => setWelcome(e.target.value)}
            data-testid="welcome-message-input"
          />
        </div>
      </div>

      <div className="flex items-center gap-4 p-4 rounded-2xl bg-sage-100 border border-sage-300/60">
        <div className="h-16 w-40 rounded-lg bg-cream-100 border border-sage-300/60 flex items-center justify-center overflow-hidden">
          {settings.logo_path ? (
            <img src={fileUrl(settings.logo_path)} alt="Logo" className="max-h-full max-w-full" />
          ) : (
            <div className="flex flex-col items-center gap-1">
              <PatriaWordmark size="sm" />
              <MyPatriaWordmark size="xs" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-forest-900">Header Logo</p>
          <p className="text-xs text-charcoal-muted">
            Falls back to <em>PATRIA</em> wordmark + <em>myPatria</em> platform mark. PNG, JPG, or WEBP up to 4MB.
          </p>
        </div>
        <button className="patria-btn-secondary" onClick={() => fileRef.current?.click()} data-testid="logo-upload-btn">
          <Upload className="h-4 w-4" strokeWidth={1.5} /> Upload
        </button>
        <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
      </div>

      <div>
        <button
          className="patria-btn-primary"
          onClick={() => onSave({ org_name: org, welcome_message: welcome })}
          data-testid="branding-save"
        >
          <Save className="h-4 w-4" strokeWidth={1.5} /> Save branding
        </button>
      </div>
    </div>
  );
}

/* -------------------- Notification templates -------------------- */
function TemplatesPanel({ settings, onSave }) {
  const [templates, setTemplates] = useState(settings.notification_templates || []);
  useEffect(() => setTemplates(settings.notification_templates || []), [settings]);

  const update = (idx, patch) =>
    setTemplates((t) => t.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  const add = () =>
    setTemplates((t) => [...t, { key: "", subject: "", body: "", description: "" }]);

  const remove = (idx) => setTemplates((t) => t.filter((_, i) => i !== idx));

  return (
    <div className="patria-card p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-serif text-forest-900">Notification Templates</h2>
        <p className="text-sm text-charcoal-soft">
          Placeholder syntax: <code className="text-forest-700">{"{name}"}</code>,{" "}
          <code className="text-forest-700">{"{org}"}</code>,{" "}
          <code className="text-forest-700">{"{link}"}</code>. Templates power both the
          in-app notification and the outbound email log entry.
        </p>
      </div>

      <ul className="space-y-4">
        {templates.map((t, idx) => (
          <li key={idx} className="rounded-2xl border border-sage-300/60 p-4 bg-sage-100/50" data-testid={`tmpl-row-${idx}`}>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <input
                className="patria-input sm:col-span-2"
                placeholder="Key (unique id)"
                value={t.key}
                onChange={(e) => update(idx, { key: e.target.value })}
                data-testid={`tmpl-key-${idx}`}
              />
              <input
                className="patria-input sm:col-span-4"
                placeholder="Subject"
                value={t.subject}
                onChange={(e) => update(idx, { subject: e.target.value })}
                data-testid={`tmpl-subject-${idx}`}
              />
              <textarea
                className="patria-input sm:col-span-6 min-h-[80px]"
                placeholder="Body"
                value={t.body}
                onChange={(e) => update(idx, { body: e.target.value })}
                data-testid={`tmpl-body-${idx}`}
              />
              <input
                className="patria-input sm:col-span-5"
                placeholder="Internal description (optional)"
                value={t.description || ""}
                onChange={(e) => update(idx, { description: e.target.value })}
              />
              <button
                className="patria-btn-ghost sm:col-span-1 text-terracotta-600"
                onClick={() => remove(idx)}
                data-testid={`tmpl-remove-${idx}`}
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Remove
              </button>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex gap-3">
        <button className="patria-btn-secondary" onClick={add} data-testid="tmpl-add">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Add template
        </button>
        <button
          className="patria-btn-primary"
          onClick={() => onSave({ notification_templates: templates })}
          data-testid="tmpl-save"
        >
          <Save className="h-4 w-4" strokeWidth={1.5} /> Save templates
        </button>
      </div>
    </div>
  );
}

/* -------------------- Outbound email log -------------------- */
function OutboxPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/outbound-email-log");
        setRows(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="patria-card p-6 space-y-4">
      <div>
        <h2 className="text-2xl font-serif text-forest-900">Outbound Email Log</h2>
        <p className="text-sm text-charcoal-soft">
          Every send attempt is recorded here — including mocked rows when
          Live Sending is off or no API key is configured. Use the Email
          Delivery tab to flip Live Sending on once your Resend API key is set.
        </p>
      </div>
      <div className="overflow-hidden rounded-2xl shadow-card">
        <table className="patria-table w-full">
          <thead>
            <tr>
              <th>When</th>
              <th>To</th>
              <th>Template</th>
              <th>Subject</th>
              <th>Event</th>
              <th>Delivery</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="italic text-charcoal-muted">
                  Loading outbox...
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="italic text-charcoal-muted">
                  Nothing sent yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`outbox-row-${r.id}`}>
                <td className="whitespace-nowrap text-xs">{formatTs(r.created_at)}</td>
                <td>{r.to}</td>
                <td>
                  <span className="patria-chip-green">{r.template_key}</span>
                </td>
                <td className="text-charcoal-soft">{r.subject}</td>
                <td className="text-xs text-charcoal-muted">{r.triggered_by_event}</td>
                <td>
                  <DeliveryStatusChip status={r.delivery_status} error={r.error_message} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeliveryStatusChip({ status, error }) {
  const s = status || "mocked_no_key";
  const cls =
    s === "sent"
      ? "patria-chip-green"
      : s === "error"
      ? "patria-chip-terra"
      : "patria-chip-neutral";
  const label = {
    sent: "Sent",
    error: "Error",
    mocked_no_key: "Mocked (no key)",
    mocked_toggle_off: "Mocked (toggle off)",
    allowlist_blocked: "Mocked (allowlist)",
  }[s] || s;
  return (
    <span className={cls} title={error || undefined} data-testid={`delivery-status-${s}`}>
      {label}
    </span>
  );
}

/* -------------------- Email delivery (Resend) -------------------- */
function EmailDeliveryPanel() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    live_sending_enabled: false,
    from_name: "",
    from_address: "",
    allowlist_text: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/email-delivery");
      setStatus(data);
      const s = data.settings || {};
      setForm({
        live_sending_enabled: !!s.live_sending_enabled,
        from_name: s.from_name || "",
        from_address: s.from_address || "",
        allowlist_text: (s.allowlist || []).join(", "),
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        live_sending_enabled: form.live_sending_enabled,
        from_name: form.from_name,
        from_address: form.from_address,
        allowlist: form.allowlist_text
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const { data } = await api.put("/settings/email-delivery", payload);
      setStatus(data);
      toast.success("Email delivery settings saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) {
      toast.error("Please enter a recipient email.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/email-delivery/test", { to: testTo.trim() });
      setTestResult(data);
      if (data.status === "sent") {
        toast.success("Test email sent — check your inbox.");
      } else {
        toast.success(`Test email recorded (${data.status}). See the Outbound Email Log tab.`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (!status) return <p className="italic text-charcoal-muted">Loading email delivery settings…</p>;

  const stateCls = {
    live: "patria-chip-green",
    mock_test_mode: "patria-chip-terra",
    mock_no_key: "patria-chip-terra",
    mock_toggle_off: "patria-chip-neutral",
  }[status.state] || "patria-chip-neutral";
  const isTestMode = status.state === "mock_test_mode";

  return (
    <div className="space-y-6" data-testid="email-delivery-panel">
      {isTestMode && (
        <div
          className="patria-card p-4 border-l-4 border-terracotta-500 bg-terracotta-500/10"
          data-testid="email-test-mode-banner"
        >
          <p className="text-[10px] uppercase tracking-widest text-terracotta-700 font-medium">
            Email Test Mode Active
          </p>
          <p className="text-sm text-charcoal mt-1">
            <strong>EMAIL_TEST_MODE=1</strong> is set on the server environment.
            Every send — including &ldquo;Live Sending&rdquo; — is being
            suppressed and logged as <code className="text-xs">mocked_test_mode</code>.
            This is the correct posture for preview / testing environments.
            Remove the env variable in production so real delivery can happen.
          </p>
        </div>
      )}
      <div className="patria-card p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-serif text-forest-900">Email Delivery</h2>
            <p className="text-sm text-charcoal-soft mt-1 max-w-2xl">
              MyPatria can send real emails through <strong>Resend</strong> once
              a <code className="text-xs">RESEND_API_KEY</code> is set on the
              backend and Live Sending is toggled on. Until then, every email
              stays in the Outbound Email Log (mock mode).
            </p>
          </div>
          <span className={stateCls} data-testid="email-delivery-state">{status.label}</span>
        </div>
      </div>

      <div className="patria-card p-6 space-y-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.live_sending_enabled}
            onChange={(e) => setForm({ ...form, live_sending_enabled: e.target.checked })}
            data-testid="live-sending-toggle"
          />
          <span className="text-forest-900 font-medium">Live Sending Enabled</span>
          <span className="text-xs text-charcoal-muted">
            (Must be on <em>and</em> an API key must be configured for real delivery.)
          </span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="patria-label">From Name</label>
            <input
              className="patria-input w-full"
              value={form.from_name}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })}
              placeholder="MyPatria"
              data-testid="from-name-input"
            />
          </div>
          <div>
            <label className="patria-label">From Address</label>
            <input
              className="patria-input w-full"
              value={form.from_address}
              onChange={(e) => setForm({ ...form, from_address: e.target.value })}
              placeholder="onboarding@resend.dev"
              data-testid="from-address-input"
            />
            <p className="text-[11px] text-charcoal-muted mt-1">
              Once <code>patriachurch.org</code> is verified in Resend, change this to
              <code> no-reply@patriachurch.org</code>.
            </p>
          </div>
        </div>

        <div>
          <label className="patria-label">Allowlist (Optional)</label>
          <textarea
            className="patria-input w-full min-h-[70px]"
            value={form.allowlist_text}
            onChange={(e) => setForm({ ...form, allowlist_text: e.target.value })}
            placeholder="admin@patriachurch.org, people@patriachurch.org"
            data-testid="allowlist-input"
          />
          <p className="text-[11px] text-charcoal-muted mt-1">
            When populated, only recipients on this list get real delivery. Everyone
            else is force-mocked with <code>allowlist_blocked</code> (row still
            written to the outbox — click <em>Outbound Email Log</em> below to
            audit). <strong>Leave blank to send to every recipient</strong> once
            you&rsquo;re confident real delivery is working.
          </p>
        </div>

        <div>
          <button onClick={save} disabled={saving} className="patria-btn-primary" data-testid="save-email-delivery">
            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>

      <div className="patria-card p-6 space-y-3">
        <h3 className="text-xl font-serif text-forest-900">Send Test Email</h3>
        <p className="text-sm text-charcoal-soft">
          Sends a warm test message using the current settings — respects the
          Live Sending toggle and the allowlist.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            className="patria-input flex-1 min-w-[220px]"
            placeholder="you@patriachurch.org"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            data-testid="test-email-to"
          />
          <button
            onClick={sendTest}
            disabled={testing}
            className="patria-btn-primary"
            data-testid="btn-send-test-email"
          >
            <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {testing ? "Sending…" : "Send Test Email"}
          </button>
        </div>
        {testResult && (
          <div className="p-3 rounded-xl bg-cream-100 text-sm text-charcoal-soft" data-testid="test-email-result">
            <p>
              <strong>Result:</strong> {testResult.status}
              {testResult.provider_message_id ? ` · id ${testResult.provider_message_id}` : ""}
            </p>
            {testResult.message && <p className="mt-1 text-terracotta-700">{testResult.message}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------- Text & Headings (UI Registry) -------------------- */
function TextHeadingsPanel() {
  const { reload } = useUiText();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/ui-text");
      setRows(data);
      setDrafts({});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) {
      (g[r.page] = g[r.page] || []).push(r);
    }
    return g;
  }, [rows]);

  const save = async (key) => {
    const text = (drafts[key] ?? "").trim();
    if (!text) { toast.error("Text can't be blank."); return; }
    setSavingKey(key);
    try {
      await api.put(`/settings/ui-text/${encodeURIComponent(key)}`, { text });
      toast.success("Saved. Reloading pages will pick it up.");
      await load();
      await reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSavingKey(null); }
  };

  const reset = async (key) => {
    setSavingKey(key);
    try {
      await api.post(`/settings/ui-text/${encodeURIComponent(key)}/reset`);
      toast.success("Reset to default.");
      await load();
      await reload();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSavingKey(null); }
  };

  const heal = async () => {
    try {
      const { data } = await api.post("/settings/ui-text/heal");
      toast.success(`Registry healed — ${data.added} new key(s).`);
      await load();
      await reload();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  if (loading) return <p className="italic text-charcoal-muted">Loading text registry…</p>;

  return (
    <div className="space-y-4" data-testid="text-headings-panel">
      <div className="patria-card p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-serif text-forest-900">Text & Headings</h2>
            <p className="text-sm text-charcoal-soft mt-1 max-w-2xl">
              Rename any page title, subtitle, module card, or dashboard heading. Your text is used
              everywhere immediately. Reset to Default restores the original wording.
            </p>
          </div>
          <button
            type="button"
            onClick={heal}
            className="patria-btn-secondary text-sm"
            data-testid="btn-ui-text-heal"
          >
            Heal Registry
          </button>
        </div>
      </div>
      {Object.entries(grouped).map(([page, items]) => (
        <div key={page} className="patria-card p-5" data-testid={`ui-text-group-${page}`}>
          <h3 className="font-serif text-xl text-forest-900 mb-3">{page}</h3>
          <ul className="space-y-3">
            {items.map((r) => {
              const draft = drafts[r.key] ?? r.effective_text;
              const dirty = draft !== r.effective_text;
              return (
                <li key={r.key} className="p-3 rounded-xl bg-cream-100" data-testid={`ui-text-row-${r.key}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                      {r.label} · <code className="text-terracotta-600">{r.key}</code>
                      {r.is_customized && <span className="patria-chip-terra text-[10px] ml-2">Custom</span>}
                    </div>
                  </div>
                  <input
                    className="patria-input w-full"
                    value={draft}
                    onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))}
                    data-testid={`ui-text-input-${r.key}`}
                  />
                  <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-[11px] text-charcoal-muted italic">
                      Default: {r.default_text}
                    </span>
                    <div className="flex gap-2">
                      {r.is_customized && (
                        <button
                          onClick={() => reset(r.key)}
                          disabled={savingKey === r.key}
                          className="patria-btn-ghost text-terracotta-600 text-xs"
                          data-testid={`ui-text-reset-${r.key}`}
                        >
                          Reset To Default
                        </button>
                      )}
                      <button
                        onClick={() => save(r.key)}
                        disabled={!dirty || savingKey === r.key}
                        className="patria-btn-primary text-xs"
                        data-testid={`ui-text-save-${r.key}`}
                      >
                        {savingKey === r.key ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

// Local helper — grabs the UI text reload fn from the context without
// forcing an import cycle if the panel is bundled independently later.
function require_uiTextHelper() {
  return { reload: async () => {} };
}

function formatTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
